import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Copy, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';

const SITE = (process.env.REACT_APP_SITE_URL || 'https://goconvertdocuments.com').replace(/\/$/, '');
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function CodeBlock({ code, label = 'code' }) {
  const copy = () => {
    navigator.clipboard.writeText(code);
    toast.success('Copied!');
  };
  return (
    <div className="border-2 border-black bg-slate-950 text-slate-100 mt-3 relative">
      <button
        data-testid={`copy-${label}`}
        onClick={copy}
        className="absolute top-2 right-2 border-2 border-white/20 bg-black/60 hover:bg-black text-white text-xs font-mono-plex px-2 py-1 flex items-center gap-1"
        style={{ transition: 'background-color 0.15s ease' }}
      >
        <Copy weight="bold" size={12} /> copy
      </button>
      <pre className="p-4 pt-10 overflow-x-auto text-xs md:text-sm font-mono-plex whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function Developers() {
  useEffect(() => {
    document.title = 'Free Document Conversion API | Go Convert Documents Developers';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Free REST API to convert PDF, Word, Excel, PowerPoint and images. curl / Node / Python examples. No API key required.');
  }, []);

  const base = API_URL || SITE;
  const curl = `curl -X POST ${base}/api/convert/pdf-to-word \\\n  -F "files=@document.pdf" \\\n  | jq`;
  const nodeExample = `import fs from 'fs';
import FormData from 'form-data';
import fetch from 'node-fetch';

const form = new FormData();
form.append('files', fs.createReadStream('./document.pdf'));

const res = await fetch('${base}/api/convert/pdf-to-word', {
  method: 'POST',
  body: form,
});
const { download_url } = await res.json();
console.log('${base}' + download_url);`;
  const pyExample = `import requests

with open('document.pdf', 'rb') as f:
    r = requests.post(
        '${base}/api/convert/pdf-to-word',
        files={'files': f},
    )
job = r.json()
print('${base}' + job['download_url'])`;

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <p className="text-xs font-mono-plex uppercase tracking-widest text-zinc-500">Developers</p>
      <h1 className="font-display font-bold text-4xl md:text-6xl leading-tight text-slate-950 mt-2">
        A free document<br />
        <span className="bg-black text-white px-2">conversion API</span>
      </h1>
      <p className="text-lg text-zinc-700 mt-6 leading-relaxed max-w-2xl">
        Every tool on Go Convert Documents is available as a plain HTTPS endpoint. No API key, no rate-limit dashboard, no monthly bill — just <code className="font-mono-plex bg-zinc-100 px-1">POST</code> your file and receive a download URL.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mt-10">
        {[
          { t: 'No key required', d: 'Public HTTPS. Just call it.' },
          { t: 'Any language', d: 'Standard multipart upload — curl, Node, Python, Go.' },
          { t: 'Auto-cleanup', d: 'Files removed from our servers after 1 hour.' },
        ].map((f) => (
          <div key={f.t} className="border-2 border-black bg-white p-5 brutal-shadow-sm">
            <div className="flex items-center gap-2">
              <CheckCircle weight="fill" className="text-emerald-500" size={18} />
              <h3 className="font-display font-semibold">{f.t}</h3>
            </div>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{f.d}</p>
          </div>
        ))}
      </div>

      <h2 className="font-display font-bold text-3xl mt-14">Endpoints</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Base URL: <code className="font-mono-plex bg-zinc-100 px-2 py-1 border border-zinc-300">{base}</code>
      </p>
      <ul className="mt-4 space-y-2 text-sm">
        <li><code className="font-mono-plex bg-zinc-100 px-2 py-0.5">GET /api/tools</code> — list every available tool with schema.</li>
        <li><code className="font-mono-plex bg-zinc-100 px-2 py-0.5">GET /api/tools/{'{id}'}</code> — one tool's schema.</li>
        <li><code className="font-mono-plex bg-zinc-100 px-2 py-0.5">POST /api/convert/{'{id}'}</code> — multipart form: <code>files</code> + option fields.</li>
        <li><code className="font-mono-plex bg-zinc-100 px-2 py-0.5">GET /api/download/{'{job_id}'}</code> — returns the converted file.</li>
      </ul>

      <h2 className="font-display font-bold text-3xl mt-14">Quick start</h2>

      <h3 className="font-display font-semibold text-xl mt-8">cURL</h3>
      <CodeBlock code={curl} label="curl" />

      <h3 className="font-display font-semibold text-xl mt-8">Node.js</h3>
      <CodeBlock code={nodeExample} label="node" />

      <h3 className="font-display font-semibold text-xl mt-8">Python</h3>
      <CodeBlock code={pyExample} label="python" />

      <h2 className="font-display font-bold text-3xl mt-14">Available tool IDs</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        Use one of these as <code className="font-mono-plex bg-zinc-100 px-1">{'{id}'}</code> in <code className="font-mono-plex bg-zinc-100 px-1">/api/convert/{'{id}'}</code>:
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {[
          'pdf-to-word','pdf-to-excel','pdf-to-powerpoint','pdf-to-text','word-to-pdf',
          'excel-to-pdf','powerpoint-to-pdf','text-to-pdf','pdf-to-jpg','pdf-to-png',
          'jpg-to-pdf','png-to-pdf','images-to-pdf','image-to-png','image-to-jpg',
          'image-to-webp','image-to-bmp','image-to-gif','merge-pdf','split-pdf',
          'compress-pdf','rotate-pdf','protect-pdf','unlock-pdf','watermark-pdf','ocr-pdf',
        ].map((id) => (
          <code key={id} className="font-mono-plex text-xs bg-zinc-100 border border-zinc-300 px-2 py-1">{id}</code>
        ))}
      </div>

      <h2 className="font-display font-bold text-3xl mt-14">Fair-use policy</h2>
      <p className="text-zinc-700 mt-3 leading-relaxed">
        The API is a public shared resource. If you plan to build a commercial product on it, please <Link to="/contact" className="underline font-semibold">get in touch</Link> so we can coordinate on rate limits and reliability.
      </p>

      <div className="mt-14 border-2 border-black bg-yellow-100 p-6 brutal-shadow">
        <p className="font-mono-plex text-xs uppercase tracking-widest text-zinc-600">Build something cool?</p>
        <p className="font-display font-semibold text-2xl mt-2">
          If your project uses our API, we&apos;ll link it back from this page.
        </p>
        <Link to="/contact" className="brutal-btn inline-flex items-center gap-2 mt-4">
          Tell us about it <ArrowRight weight="bold" size={18} />
        </Link>
      </div>
    </div>
  );
}
